
import { defineComponent, reactive, onMounted, watch, ref, Ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Subreddit } from '@/typings';
import store from '@/store';

import Navbar from '@/components/Navbar.vue';
import Multiselect from '@vueform/multiselect';
import FullMDEditor from '@/components/CreatePost/FullMDEditor.vue';
import SubredditSidebar from '@/components/SubredditSidebar.vue';
import InfoSidebar from '@/components/InfoSidebar.vue';
import Topbar from '@/components/Subreddit/Topbar.vue';

// @ts-ignore
import taskLists from 'markdown-it-task-lists';

export default defineComponent({
	components: {
		Navbar,
		SubredditSidebar,
		Multiselect,
		FullMDEditor,
		InfoSidebar,
		Topbar,
	},
	setup() {
		document.title = 'Create Post | (not) reddit';
		const router = useRouter();
		const route = useRoute();
		const mdEditor: Ref<HTMLTextAreaElement | null> = ref(null);

		const local: {
			subreddit: Partial<Subreddit> | null;
			mdPreview: boolean;
			submittingPost: boolean;
		} = reactive({
			subreddit: null,
			mdPreview: false,
			submittingPost: false,
		});

		const postInput = ref(store.createPost.state.newPost);
		const subreddits = ref(store.subreddits.state.subreddits);

		onMounted(async () => {
			if (route.params.name && route.params.r) {
				const subreddit = ref(store.subreddit.state.subreddit);
				store.subreddit.state.menu = 'create';
				if (store.subreddit.state.subredditLoaded) {
					postInput.value.subreddit = `r/${subreddit.value.name}`;
					local.subreddit = subreddit.value;
				} else router.push(`/r/${route.params.name}`);
			}

			watch(ref(postInput.value.subreddit), () => {
				if (!postInput.value.subreddit) return;
				local.subreddit =
					subreddits.value.find(
						(x) =>
							x.name.toLowerCase() ===
							postInput.value.subreddit
								.toLowerCase()
								.replace(/r\//g, '')
					) ?? store.subreddits.state.subreddits[0];
			});

			watch(ref(postInput.value.content), () => {
				if (!mdEditor.value) return;
				mdEditor.value.style.height = 'auto';
				mdEditor.value.style.height = `${mdEditor.value.scrollHeight}px`;
			});
		});

		// minimise this
		function textAreaHandler(e: KeyboardEvent) {
			if (!mdEditor.value) return;
			let contentArr = [...mdEditor.value.value];

			const delimiters: any = {
				//let's me use [] without writing a type
				always: {
					'"': '"',
					'(': ')',
					'{': '}',
					'[': ']',
					'<': '>',
					'`': '`',
				},
				nextToWhitespace: {
					"'": "'",
					_: '_',
					'*': '*',
					'~': '~',
				},
			};

			const ctrlChars: any = {
				b: '**',
				i: '_',
			};

			const cursorStart = mdEditor.value.selectionStart;
			const cursorEnd = mdEditor.value.selectionEnd;

			// handle selection delimiters
			if (cursorStart !== cursorEnd) {
				if (e.key === 'Backspace') return;

				if (e.ctrlKey && e.key in ctrlChars) {
					e.preventDefault();
					contentArr.splice(cursorStart, 0, ctrlChars[e.key]);
					contentArr.splice(cursorEnd + 1, 0, ctrlChars[e.key]);
					mdEditor.value.value = contentArr.join('');
					mdEditor.value.selectionStart = cursorStart;
					mdEditor.value.selectionEnd =
						cursorEnd + ctrlChars[e.key].length * 2;
					return;
				}

				if (
					!(
						e.key in delimiters.always ||
						e.key in delimiters.nextToWhitespace
					)
				)
					return;
				e.preventDefault();
				const delimiter1 =
					delimiters.always[e.key] ||
					delimiters.nextToWhitespace[e.key];

				contentArr.splice(cursorStart, 0, e.key);
				contentArr.splice(cursorEnd + 1, 0, delimiter1);
				mdEditor.value.value = contentArr.join('');

				mdEditor.value.selectionStart = cursorStart;
				mdEditor.value.selectionEnd = cursorEnd + 2;

				return;
			}

			// handle regular delimiters
			if (e.key === 'Backspace') {
				if (
					!(
						(contentArr[cursorStart] in delimiters.always ||
							contentArr[cursorStart] in
								delimiters.nextToWhitespace) &&
						(contentArr[cursorStart - 1] in delimiters.always ||
							contentArr[cursorStart - 1] in
								delimiters.nextToWhitespace)
					)
				)
					return;
				contentArr.splice(cursorStart, 1);
			} else if (e.ctrlKey && e.key in ctrlChars) {
				e.preventDefault();
				contentArr.splice(
					cursorStart,
					0,
					ctrlChars[e.key] + ctrlChars[e.key]
				);
				mdEditor.value.value = contentArr.join('');
				mdEditor.value.selectionStart =
					cursorStart + ctrlChars[e.key].length;
				mdEditor.value.selectionEnd =
					cursorStart + ctrlChars[e.key].length;
				return;
			} else if (e.key in delimiters.always) {
				contentArr.splice(cursorStart, 0, delimiters.always[e.key]);
			} else if (e.key in delimiters.nextToWhitespace) {
				if (/^\S$/g.test(contentArr[cursorStart - 1])) return;
				contentArr.splice(
					cursorStart,
					0,
					delimiters.nextToWhitespace[e.key]
				);
			} else return;

			mdEditor.value.value = contentArr.join('');
			mdEditor.value.selectionEnd = cursorStart;
			mdEditor.value.selectionStart = cursorStart;
		}

		async function createPost() {
			if (!store.auth.state.isLoggedIn) return;
			if (!postInput.value.title.length)
				return alert('A title is required.');
			const subreddit = store.subreddits.state.subreddits.find(
				(x) =>
					x.name.toLowerCase() ===
					postInput.value.subreddit.toLowerCase().replace('r/', '')
			);

			if (!subreddit)
				return alert(
					"This subreddit doesn't exist or could not be found."
				);

			local.submittingPost = true;

			const submitButton = document.getElementById(
				'submitPost'
			) as HTMLButtonElement;
			submitButton.disabled = true;

			const res = await store.createPost.actions.createPost({
				title: postInput.value.title,
				content: postInput.value.content,
				subreddit_id: subreddit.id,
				user_id: store.auth.state.user?.id,
			});

			local.submittingPost = false;
			submitButton.disabled = false;

			postInput.value.title = '';
			postInput.value.content = '';

			if (res) {
				let subredditToPush = postInput.value.subreddit;
				router.push({ path: `/${subredditToPush}` });
			}
		}

		return {
			store,
			router,
			route,
			postInput,
			subreddits,
			local,
			textAreaHandler,
			mdEditor,
			createPost,
			taskLists,
		};
	},
});
